import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import { Card, CardBody, CardHeader } from "reactstrap";
import { constants } from '../../../config';
import { layoutModeTypes } from "../../constants/layout";
import { getHTMLAttribute } from "../../../slices/layouts/utils";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";



// Establece el token de acceso de Mapbox
mapboxgl.accessToken = constants.REACT_APP_MAPBOX;

const MapCluster = ({ title, points, height = '200px', zoom = 4 }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);

    const selectLayoutState = (state) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({ layoutModeType: layout.layoutModeType })
    );

    const { layoutModeType } = useSelector(selectLayoutProperties);

    useEffect(() => {
        const mapStyle = layoutModeType === 'dark'
            ? 'mapbox://styles/mapbox/dark-v10'
            : 'mapbox://styles/mapbox/light-v11';

        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapStyle,
            center: [-72.9411, -41.4689],
            zoom: zoom,
        });

        mapRef.current = map;

        map.on('load', () => {
            if (points && points.length > 0) {
                const bounds = new mapboxgl.LngLatBounds();

                points.forEach(point => {
                    bounds.extend([point.longitude, point.latitude]);
                });

                map.fitBounds(bounds, {
                    padding: { top: 100, bottom: 100, left: 100, right: 100 },
                });

                map.addSource('points', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: points.map(point => {
                            if (!point.longitude || !point.latitude) {
                                console.warn(`Invalid coordinates for point: ${JSON.stringify(point)}`);
                                return null;
                            }
                            return {
                                type: 'Feature',
                                properties: {
                                    info: point.info,
                                    tn: point.total_tn,
                                },
                                geometry: {
                                    type: 'Point',
                                    coordinates: [point.longitude, point.latitude],
                                },
                            };
                        }).filter(feature => feature !== null),
                    },
                    cluster: true,
                    clusterMaxZoom: 14,
                    clusterRadius: 50,
                });

                map.addLayer({
                    id: 'clusters',
                    type: 'circle',
                    source: 'points',
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': [
                            'step',
                            ['get', 'point_count'],
                            '#51bbd6',
                            100,
                            '#f1f075',
                            750,
                            '#f28cb1',
                        ],
                        'circle-radius': [
                            'step',
                            ['get', 'point_count'],
                            20,
                            100,
                            30,
                            750,
                            40,
                        ],
                    },
                });

                map.addLayer({
                    id: 'cluster-count',
                    type: 'symbol',
                    source: 'points',
                    filter: ['has', 'point_count'],
                    layout: {
                        'text-field': '{point_count_abbreviated}',
                        'text-size': 12,
                    },
                });

                map.addLayer({
                    id: 'unclustered-point',
                    type: 'circle',
                    source: 'points',
                    filter: ['!', ['has', 'point_count']],
                    paint: {
                        'circle-color': '#11b4da',
                        'circle-radius': [
                            'interpolate',
                            ['linear'],
                            ['get', 'tn'],
                            1, 5,
                            10, 10,
                            100, 15,
                            1000, 20,
                        ],
                        'circle-stroke-width': 1,
                        'circle-stroke-color': '#fff',
                    },
                });

                map.on('click', 'clusters', e => {
                    const features = map.queryRenderedFeatures(e.point, {
                        layers: ['clusters'],
                    });
                    const clusterId = features[0].properties.cluster_id;
                    map.getSource('points').getClusterExpansionZoom(clusterId, (err, zoom) => {
                        if (err) return;

                        map.easeTo({
                            center: features[0].geometry.coordinates,
                            zoom: zoom,
                        });
                    });
                });

                map.on('click', 'unclustered-point', e => {
                    const coordinates = e.features[0].geometry.coordinates.slice();
                    const { info } = e.features[0].properties;

                    new mapboxgl.Popup()
                        .setLngLat(coordinates)
                        .setHTML(info)
                        .addTo(map);
                });

                map.on('mouseenter', 'clusters', () => {
                    map.getCanvas().style.cursor = 'pointer';
                });
                map.on('mouseleave', 'clusters', () => {
                    map.getCanvas().style.cursor = '';
                });
            }
        });

        return () => map.remove();
    }, [points, zoom, layoutModeType]);

    return (
        <Card className='p-0 mb-10 card-height-100'>
            {title && (
                <CardHeader className='p-2'>
                    <h5 className="card-title mb-0">
                        <i className={`${Icons.pin} align-middle me-1 text-muted`}></i>{title}
                    </h5>
                </CardHeader>
            )}
            <CardBody className='m-0 p-2'>
                <div ref={mapContainerRef} style={{ width: '100%', height: height, minHeight: '100%' }} />
            </CardBody>
        </Card>
    );
};

export default MapCluster;
